import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View, useWindowDimensions } from 'react-native';
import Animated, {
    Easing,
    FadeInDown,
    runOnJS,
    useAnimatedStyle,
    useSharedValue,
    withDelay,
    withTiming,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { NavigationState, PartialState } from '@react-navigation/native';

import { WelcomeRipsIcon } from '@/assets/icons/WelcomeRipsIcon';
import WelcomeArcadeIcon from '@/assets/icons/welcomeArcade';
import WelcomeCasinoIcon from '@/assets/icons/welcomeCasino';
import WelcomePicksIcon from '@/assets/icons/welcomePicks';
import WelcomePredictionIcon from '@/assets/icons/welcomePredictions';
import WelcomeSweepsIcon from '@/assets/icons/welcomeSweeps';
import { CustomFullWindowOverlay } from '@/components/CustomFullWindowOverlay';
import { ModalPullDownIndicator } from '@/components/PullDownIndicator';
import { Text } from '@/components/TextComponent';
import { Box } from '@/components/lib/components/Box';
import { ds } from '@/components/lib/design.system';
import { LOBBY_HANDLE_HEIGHT, productSwitcherStyles } from '@/feature/product-transition/components/LobbyWrapper';
import {
    PRODUCT_CHIPS_HEIGHT,
    ProductSwitcherChipsTransition,
} from '@/feature/product-transition/components/ProductSwitcherChips';
import { useActiveWalletStore } from '@/hooks/use-active-wallet';
import { Product, useJurisdictionStore } from '@/hooks/use-jurisdiction';
import { navigationRef } from '@/navigation/navigation';
import { common, designSystem } from '@/styles/styles';
import { MAX_WEB_WIDTH, isWeb } from '@/utils/constants-platform-specific';
import { getProductLobby, getProductRoot } from '@/utils/product';
import { runAfterInteractions } from '@/utils/runAfterInteractions';

import { MaxWidthWrapper } from '../responsive-design/WebComponents';
import { useProductTransitionStore } from './use-product-transition-store';

export const FADE_IN_DURATION = 300;
export const FADE_OUT_DURATION = 300;
export const FADE_OUT_DELAY = 400;
export const NAVIGATION_DELAY = 50;

/**
 * Overlay that fades in over the active product during a product switch
 * It contains the "Switching to..." animation and controls the full transition flow
 *
 * When mounted, it immediately starts the fade-in animation. The sequence is:
 * - fade in the overlay (shows the product name and welcome icon)
 * - onFadeInComplete:
 *     - set isFadingOut so the chip bar stops animating, avoiding a visual jump
 *       when the nav reset re-mounts the lobby behind the overlay
 *     - reset the active wallet so the new lobby starts clean
 *     - set the new product in the jurisdiction store
 *     - reset the nav stack to the new product lobby in a single call
 *       (the old ProductTransitionScreen needed two resets to avoid flickering;
 *       since the overlay is outside the navigator, one reset is enough).
 *       The old routes close from the top first, see closeOldRoutes
 * - fade out after FADE_OUT_DELAY, giving the new lobby time to mount
 * - onFadeOutComplete:
 *     - navigate to targetScreen if one was given and the new product is not in maintenance
 *     - call completeTransition, which unmounts the overlay
 *
 * Store state is read via getState() inside the effect rather than from React props/state.
 * The effect runs once on mount and must not re-run, so reading a stable snapshot avoids
 * stale-closure bugs and prevents the animation from being cancelled by a re-render.
 */
const OverlayContent = () => {
    const { t } = useTranslation('kyc');
    // read snapshot at render time for display, these values do not change during a transition
    const product = useProductTransitionStore(state => state.product);
    const previousProduct = useProductTransitionStore(state => state.previousProduct);

    const [isFadingOut, setIsFadingOut] = useState(false);
    const fadeOpacity = useSharedValue(0);

    useEffect(() => {
        // read the store snapshot directly so the animation cannot be cancelled by a re-render
        const {
            product: toProduct,
            targetScreen,
            actions: { completeTransition },
        } = useProductTransitionStore.getState();

        function onFadeOutComplete() {
            const isInMaintenanceMode =
                useJurisdictionStore.getState().jurisdictionSettings?.productConfig?.appMaintenanceConfig?.primary
                    ?.enabled || false;
            // in maintenance the user is already on MaintenanceScreen, so don't navigate away from it,
            // but still complete the transition so the overlay unmounts and stops eating touches
            if (targetScreen && !isInMaintenanceMode) {
                runAfterInteractions(() => {
                    (navigationRef.navigate as (name: string, params?: object) => void)(
                        targetScreen.name,
                        targetScreen.params
                    );
                });
            }

            completeTransition();
        }

        function onFadeInComplete() {
            setIsFadingOut(true);
            // Update product and reset wallet right before the nav reset so the old lobby's
            // chips don't react to the product change while the fade-in is still running.
            useActiveWalletStore.getState().resetActiveWallet();
            useJurisdictionStore.getState().actions.setProduct(toProduct);
            const root = getProductRoot(toProduct);

            const resetToProductRoot = () => {
                navigationRef.reset({
                    index: 0,
                    routes: [{ name: root, params: { screen: getProductLobby(toProduct).tab } }],
                });
                fadeOpacity.value = withDelay(
                    FADE_OUT_DELAY + NAVIGATION_DELAY,
                    withTiming(0, { duration: FADE_OUT_DURATION, easing: Easing.linear }, () => {
                        runOnJS(onFadeOutComplete)();
                    })
                );
            };

            // on iOS, stacked full screen covers need to be closed one by one before the reset,
            // otherwise the top one slides down and shows through the fade out
            const closeOldRoutes = () => {
                const rootRoutes = navigationRef.getRootState()?.routes ?? [];
                // highest route below the top that has its own navigator (nested state)
                const navigatorIndex = rootRoutes
                    .slice(0, -1)
                    .map(({ state }) => state !== undefined)
                    .lastIndexOf(true);

                if (navigatorIndex === -1) {
                    resetToProductRoot();
                    return;
                }

                navigationRef.reset({
                    index: navigatorIndex,
                    routes: rootRoutes.slice(0, navigatorIndex + 1),
                } as PartialState<NavigationState>);

                setTimeout(closeOldRoutes, 0);
            };

            closeOldRoutes();
        }

        fadeOpacity.value = withTiming(1, { duration: FADE_IN_DURATION, easing: Easing.linear }, () => {
            runOnJS(onFadeInComplete)();
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const animatedStyle = useAnimatedStyle(() => ({ opacity: fadeOpacity.value }));
    const insets = useSafeAreaInsets();
    const { height } = useWindowDimensions();

    return (
        <Box flex={1} style={styles.screenContainer}>
            <Box
                style={{ paddingTop: insets.top }}
                overflow={'hidden'}
                height={insets.top + PRODUCT_CHIPS_HEIGHT + LOBBY_HANDLE_HEIGHT}
                backgroundColor={ds.colorToken.background.brand.strong}
            >
                <MaxWidthWrapper>
                    <ProductSwitcherChipsTransition
                        fromProduct={previousProduct}
                        toProduct={product}
                        skipAnimation={isFadingOut}
                    />
                </MaxWidthWrapper>
                <Box style={isWeb ? {} : productSwitcherStyles.lobbySheetShadow} height={height} width="100%">
                    <Box
                        height={height}
                        flex={1}
                        backgroundColor={ds.colorToken.background.neutral.base}
                        style={productSwitcherStyles.borderRadius}
                        overflow={'hidden'}
                    >
                        <Box top={12} width={'100%'}>
                            <ModalPullDownIndicator color={designSystem.colors.neutral500} />
                        </Box>
                    </Box>
                </Box>
            </Box>

            <Animated.View style={[styles.container, animatedStyle]} key="container">
                <Animated.View
                    key="text"
                    style={common.alignCenter}
                    entering={FadeInDown.duration(FADE_IN_DURATION).delay(100)}
                >
                    <Box maxWidth={MAX_WEB_WIDTH} width={'100%'} alignItems="center">
                        <Text variant={'bodyLarge'} marginBottom={'s8'} color={'neutral300'}>
                            {t('switching_to')}
                        </Text>
                        {product === Product.Pickem ? (
                            <WelcomePicksIcon />
                        ) : product === Product.Arcade ? (
                            <WelcomeArcadeIcon />
                        ) : product === Product.Sweeps ? (
                            <WelcomeSweepsIcon />
                        ) : product === Product.Casino ? (
                            <WelcomeCasinoIcon />
                        ) : product === Product.Prediction ? (
                            <WelcomePredictionIcon />
                        ) : product === Product.Rips ? (
                            <WelcomeRipsIcon />
                        ) : null}
                    </Box>
                </Animated.View>
            </Animated.View>
        </Box>
    );
};

/**
 * Renders above the entire navigator tree while a product switch is in progress.
 * It must be placed outside the NavigationContainer so nav resets do not unmount it.
 * CustomFullWindowOverlay is used so the overlay sits above native modal screens on iOS,
 * and falls back to an absolute-fill view with a high z-index on Android.
 */
export const ProductTransitionOverlay = () => {
    const isPending = useProductTransitionStore(state => state.isPending);

    if (!isPending) return null;

    return (
        <CustomFullWindowOverlay>
            <View style={StyleSheet.absoluteFill}>
                <OverlayContent />
            </View>
        </CustomFullWindowOverlay>
    );
};

const styles = StyleSheet.create({
    container: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        flex: 1,
        justifyContent: 'center',
        backgroundColor: ds.colorValue.background.neutral.base,
        zIndex: -1,
    },
    screenContainer: {
        // force scroll on web to avoid content jumping around
        // @ts-ignore: 'overflow-y' is supported by react-native-web
        'overflow-y': 'scroll',
    },
});
